import { create } from 'zustand'
import { apiAdapter } from '@/lib/api-adapter'

// 客户端帖子结构（兼容现有页面组件）
export interface Post {
  id: string
  userId: string
  category: string
  title?: string
  content: string
  images: string[]
  tags: string[]
  location?: string
  school: string
  price?: string
  condition?: string
  contactInfo?: string
  isAnonymous: boolean
  createdAt: string
  updatedAt: string
  likes: number
  comments: number
  views: number
  status: 'pending' | 'approved' | 'rejected'
  user: {
    id: string
    username: string
    avatar?: string
    level: number
    school: string
  }
  isLiked?: boolean
  likedBy?: string[]
}

export interface CreatePostData {
  userId?: string
  category: string
  title?: string
  content: string
  images: string[]
  tags: string[]
  location?: string
  school?: string
  price?: string
  condition?: string
  contactInfo?: string
  isAnonymous: boolean
  scheduledDate?: string | null
  scheduledTime?: string | null
}

interface ContentState {
  posts: Post[]
  isLoading: boolean
  fetchPosts: (filters?: { category?: string; sort?: string }) => Promise<void>
  createPost: (postData: CreatePostData) => Promise<void>
  updatePost: (id: string, data: Partial<Post>) => Promise<void>
  deletePost: (id: string) => Promise<void>
  likePost: (id: string) => Promise<void>
  getPosts: (filters?: { category?: string; school?: string }) => Post[]
  getPostById: (id: string) => Post | undefined
}

// 后端帖子 → 客户端结构映射
const fromServer = (p: any): Post => {
  const isAnonymous = !!p.isAnonymous
  const authorId = p.author?._id || p.author || ''
  return {
    id: p._id,
    userId: authorId,
    category: p.category || '其他',
    title: p.title || '',
    content: p.content || '',
    images: p.images || [],
    tags: p.tags || [],
    location: '',
    school: '',
    price: undefined,
    condition: undefined,
    contactInfo: undefined,
    isAnonymous,
    createdAt: p.createdAt,
    updatedAt: p.updatedAt,
    likes: p.likeCount || 0,
    comments: p.commentCount || 0,
    views: p.viewCount || 0,
    status: p.status === 'published' ? 'approved' : 'pending',
    user: {
      id: authorId,
      username: p.author?.nickname || p.author?.username || (isAnonymous ? '匿名用户' : '未知用户'),
      avatar: p.author?.avatar || '',
      level: 1,
      school: ''
    },
    isLiked: false,
    likedBy: p.likedBy || []
  }
}

export const useContentStore = create<ContentState>()((set, get) => ({
  posts: [],
  isLoading: false,

  fetchPosts: async (filters) => {
    set({ isLoading: true })
    try {
      const response = await apiAdapter.posts.getPosts({
        page: 1,
        limit: 50,
        category: filters?.category && filters.category !== '全部' ? filters.category : undefined,
        sort: filters?.sort
      })

      if (response.success && response.data) {
        const serverPosts = response.data.posts || []
        set({ posts: serverPosts.map(fromServer), isLoading: false })
      } else {
        set({ isLoading: false })
        throw new Error(response.message || '获取帖子失败')
      }
    } catch (error) {
      set({ isLoading: false })
      throw error
    }
  },

  createPost: async (postData) => {
    set({ isLoading: true })
    try {
      const response = await apiAdapter.posts.createPost({
        title: postData.title || '',
        content: postData.content,
        category: postData.category,
        tags: postData.tags || [],
        images: postData.images || [],
        isAnonymous: postData.isAnonymous || false
      })

      if (!response.success) {
        set({ isLoading: false })
        throw new Error(response.message || '发布失败')
      }

      // 发布成功后重新拉取最新列表（修复原版发布后清空列表的 bug）
      await get().fetchPosts()
      set({ isLoading: false })
    } catch (error) {
      set({ isLoading: false })
      throw error
    }
  },

  updatePost: async (id, data) => {
    set({ isLoading: true })
    try {
      const response = await apiAdapter.posts.updatePost(id, {
        title: data.title,
        content: data.content,
        category: data.category,
        tags: data.tags,
        images: data.images
      })
      if (!response.success) {
        set({ isLoading: false })
        throw new Error(response.message || '更新失败')
      }
      set(state => ({
        posts: state.posts.map(post =>
          post.id === id ? { ...post, ...data, updatedAt: new Date().toISOString() } : post
        ),
        isLoading: false
      }))
    } catch (error) {
      set({ isLoading: false })
      throw error
    }
  },

  deletePost: async (id) => {
    set({ isLoading: true })
    try {
      const response = await apiAdapter.posts.deletePost(id)
      if (!response.success) {
        set({ isLoading: false })
        throw new Error(response.message || '删除失败')
      }
      set(state => ({
        posts: state.posts.filter(post => post.id !== id),
        isLoading: false
      }))
    } catch (error) {
      set({ isLoading: false })
      throw error
    }
  },

  likePost: async (id) => {
    try {
      const response = await apiAdapter.posts.likePost(id)
      if (response.success && response.data) {
        const { isLiked, likeCount } = response.data
        set(state => ({
          posts: state.posts.map(post =>
            post.id === id
              ? { ...post, likes: likeCount ?? post.likes, isLiked }
              : post
          )
        }))
      }
    } catch (error) {
      throw error
    }
  },

  getPosts: (filters) => {
    const { posts } = get()
    let filteredPosts = posts.filter(post => post.status === 'approved')

    if (filters?.category && filters.category !== '全部') {
      filteredPosts = filteredPosts.filter(post => post.category === filters.category)
    }

    if (filters?.school && filters.school !== '全部学校') {
      filteredPosts = filteredPosts.filter(post => post.school === filters.school)
    }

    return filteredPosts.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
  },

  getPostById: (id) => {
    const { posts } = get()
    return posts.find(post => post.id === id)
  }
}))
